import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useElectron } from '@/context/ElectronContext';
import {
  useTransferManagerContext,
  TransferJobStatus,
} from '@/context/TransferManagerContext';
import {
  GetProjectDocument,
  GetProjectLibraryFilesDocument,
  type GetProjectQueryData,
  type GetProjectLibraryFilesQueryData,
} from '@/generated/graphql';
import type { FileStatus, ProjectFileType } from '@/hooks/useProjectSync';
import { castFileStatuses } from '@/common/utils/statusCastingUtils';
import { assertUniqueFilePaths } from '@/common/utils/projectManifestUtils';
import { createTransferDiagnostics } from '@/services/transferDiagnostics';

export interface ProjectChangeSnapshot {
  files: ProjectFileType[];
  statuses: FileStatus[];
  version: number;
  checkedAt: number;
}

/** Read-only scans of the latest cloud version, never the historical selection. */
export function useProjectChangeScan({
  projectId,
  directory,
  background = false,
  paused = false,
}: {
  projectId?: string;
  directory: string | FileSystemDirectoryHandle | null;
  background?: boolean;
  paused?: boolean;
}) {
  const client = useApolloClient();
  const { isElectron, electronAPI } = useElectron();
  const { queue } = useTransferManagerContext();
  const path = typeof directory === 'string' ? directory : null;
  const available = !!(isElectron && electronAPI && projectId && path);
  const transferActive = queue.some(
    (job) =>
      job.projectId === projectId &&
      (job.status === TransferJobStatus.ACTIVE ||
        job.status === TransferJobStatus.QUEUED)
  );
  const session = useMemo(
    () => ({ client, electronAPI, projectId, path }),
    [client, electronAPI, projectId, path]
  );
  const lifecycle = useRef<{
    session: typeof session;
    active: boolean;
    pending: Promise<ProjectChangeSnapshot | null> | null;
  } | null>(null);
  const [state, setState] = useState<{
    session: typeof session;
    scanning: boolean;
    snapshot?: ProjectChangeSnapshot;
    error?: string;
  }>({ session, scanning: false });

  useEffect(() => {
    const current = { session, active: true, pending: null } as NonNullable<
      typeof lifecycle.current
    >;
    lifecycle.current = current;
    return () => {
      current.active = false;
    };
  }, [session]);

  // Transfer jobs own their scan lifetime; UI scans still ignore stale sessions.
  const scan = useCallback(
    (detached = false): Promise<ProjectChangeSnapshot | null> => {
      const current = detached
        ? {
            session,
            active: true,
            pending: null as Promise<ProjectChangeSnapshot | null> | null,
          }
        : lifecycle.current;
      if (
        !available ||
        !projectId ||
        !path ||
        !electronAPI ||
        !current?.active ||
        current.session !== session
      )
        return Promise.resolve(null);
      if (current.pending) return current.pending;
      if (!detached)
        setState((previous) => ({
          session,
          scanning: true,
          snapshot:
            previous.session === session ? previous.snapshot : undefined,
        }));
      current.pending = (async () => {
        const diagnostics = createTransferDiagnostics('Background scan', {
          projectId,
        });
        try {
          // Fresh metadata prevents comparing with an old cloud manifest after
          // another collaborator uploads. No waveform payload is needed here.
          const metadata = await client.query<GetProjectQueryData>({
            query: GetProjectDocument,
            variables: { projectId },
            fetchPolicy: 'no-cache',
          });
          if (!metadata.data?.project)
            throw new Error('Cloud project is unavailable.');
          const version = Math.max(
            0,
            ...metadata.data.project.versionHistory.map((item) => item.version)
          );
          if (!current.active) return null;
          const manifest = version
            ? await client.query<GetProjectLibraryFilesQueryData>({
                query: GetProjectLibraryFilesDocument,
                variables: { projectId, version },
                fetchPolicy: 'no-cache',
              })
            : null;
          if (version && !manifest?.data?.projectVersion?.files)
            throw new Error('Cloud file list is unavailable.');
          const files = manifest?.data?.projectVersion?.files ?? [];
          diagnostics.update({ version, files });
          assertUniqueFilePaths(files);
          if (!current.active) return null;
          const result = await electronAPI.requestSyncCheck(
            projectId,
            path,
            files
          );
          if (!result.success || !result.fileStatuses)
            throw new Error(result.error || 'Could not scan local files.');
          const snapshot = {
            files,
            statuses: castFileStatuses(result.fileStatuses) ?? [],
            version,
            checkedAt: Date.now(),
          };
          if (!current.active) return null;
          for (const file of snapshot.statuses.filter(
            (file) => file.status === 'unknown'
          )) {
            diagnostics.add(
              'Read / hash local file',
              'File could not be safely read or classified. Retry after the DAW finishes saving.',
              {
                ...files.find(
                  (cloudFile) => cloudFile.fileName === file.fileName
                ),
                ...file,
              }
            );
          }
          if (!detached) diagnostics.publish();
          if (!detached) setState({ session, scanning: false, snapshot });
          return snapshot;
        } catch (error) {
          if (detached) throw error;
          if (current.active) {
            diagnostics.add('Fetch manifest / scan local files', error);
            diagnostics.publish();
          }
          if (!detached && current.active)
            setState({
              session,
              scanning: false,
              error:
                error instanceof Error ? error.message : 'Change scan failed.',
            });
          return null;
        } finally {
          current.pending = null;
        }
      })();
      return current.pending;
    },
    [available, projectId, path, electronAPI, client, session]
  );
  const scanForTransfer = useCallback(() => scan(true), [scan]);

  useEffect(() => {
    if (!background || !available || paused || transferActive) return;
    let stopped = false;
    let running = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      if (running) return;
      running = true;
      const started = Date.now();
      if (!document.hidden) await scan();
      running = false;
      // Schedule after completion; slow scans automatically get more idle time.
      if (!stopped)
        timer = setTimeout(tick, Math.max(60_000, (Date.now() - started) * 4));
    };
    const onVisible = () => {
      if (document.hidden) return;
      clearTimeout(timer);
      if (!running) void tick();
    };
    timer = setTimeout(tick, 1500);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [available, background, paused, transferActive, scan, session]);

  const current = state.session === session && available ? state : undefined;
  return {
    available,
    scan,
    scanForTransfer,
    transferActive,
    scanning: current?.scanning ?? false,
    snapshot: current?.snapshot,
    error: current?.error,
  };
}
